import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AirportAutocomplete } from './AirportAutocomplete';
import { PersonnelAutocomplete } from '../../../components/PersonnelAutocomplete';
import type {
  AirCabinType,
  AirSearchRequest,
  AirSearchSegment,
  AirTripType,
  AirportOption,
} from '../../../api/types/air';
import type { CorporateEmployee } from '../../../api/types/corporateEmployee';

const CABIN_TYPES = [
  { value: 'Economy', label: 'Ekonomi' },
  { value: 'Business', label: 'Business' },
];

/** Çok noktalı aramada en az 2, en fazla bu kadar bacak. */
const MAX_MULTICITY_LEGS = 6;

/**
 * `Airport/GetWithQuery` synthesizes a 4-letter "all airports in this city" pseudo
 * code by appending "A" to the real 3-letter city code (e.g. city code `IST` ->
 * `ISTA`, confirmed live: `{Code:"ISTA", CityCode:"IST", IsCity:"True", Display:
 * "İstanbul, Türkiye (Tüm Havalimanları)"}`) — needed there only so the city-wide
 * option doesn't collide with a same-named real airport that happens to share the
 * city's 3-letter code (Istanbul's own main airport is *also* coded `IST`). `Air/
 * Search` itself has no such 4-letter code — sending `ISTA` as `airportCode` there
 * isn't correct. Strips that synthetic `A` back off before building the request:
 * 4-char code ending in `A` -> the 3-char code with `isCity` forced `true`,
 * otherwise the option's own `code`/`isCity` pass through unchanged. */
function toSearchAirport(option: AirportOption): { airportCode: string; isCity: boolean } {
  const code = option.code ?? '';
  if (code.length === 4 && code.endsWith('A')) {
    return { airportCode: code.slice(0, 3), isCity: true };
  }
  return { airportCode: code, isCity: option.isCity === 'True' };
}

interface MultiCityLeg {
  origin: AirportOption | null;
  destination: AirportOption | null;
  date: Dayjs | null;
}

export function AirSearchForm({
  loading,
  onSearch,
}: {
  loading: boolean;
  onSearch: (request: AirSearchRequest) => void;
}) {
  const [tripType, setTripType] = useState<AirTripType>('OneWay');
  const [origin, setOrigin] = useState<AirportOption | null>(null);
  const [destination, setDestination] = useState<AirportOption | null>(null);
  const [departureDate, setDepartureDate] = useState<Dayjs | null>(dayjs());
  const [returnDate, setReturnDate] = useState<Dayjs | null>(dayjs());
  const [legs, setLegs] = useState<MultiCityLeg[]>([
    { origin: null, destination: null, date: dayjs() },
    { origin: null, destination: null, date: dayjs().add(1, 'day') },
  ]);
  const [adultCount, setAdultCount] = useState(1);
  const [childCount, setChildCount] = useState(0);
  const [infantCount, setInfantCount] = useState(0);
  const [cabinType, setCabinType] = useState('Economy');
  const [employee, setEmployee] = useState<CorporateEmployee | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleTripTypeChange = (value: AirTripType) => {
    // Çok noktalıya geçerken ilk bacağı tekli formdaki seçimle önden doldur
    if (value === 'MultiCity' && (origin || destination)) {
      setLegs((prev) => {
        const next = [...prev];
        next[0] = { origin, destination, date: departureDate ?? dayjs() };
        return next;
      });
    }
    setTripType(value);
  };

  const updateLeg = (index: number, patch: Partial<MultiCityLeg>) => {
    setLegs((prev) => prev.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)));
  };

  const addLeg = () => {
    setLegs((prev) => {
      if (prev.length >= MAX_MULTICITY_LEGS) return prev;
      const last = prev[prev.length - 1];
      return [
        ...prev,
        { origin: last?.destination ?? null, destination: null, date: last?.date ?? dayjs() },
      ];
    });
  };

  const removeLeg = (index: number) => {
    setLegs((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
  };

  const buildCommon = (): Pick<AirSearchRequest, 'airSearchOptions' | 'airSearchPaxItems'> => ({
    airSearchOptions: {
      flightType: tripType === 'RoundTrip' ? 'RT' : tripType === 'MultiCity' ? 'MULTI' : 'OW',
      cabinType: cabinType as AirCabinType,
      currency: 'TRY',
      employeeID: employee?.employeeID,
    },
    airSearchPaxItems: [
      { paxCode: 'ADT', paxCount: adultCount },
      ...(childCount > 0 ? [{ paxCode: 'CHD', paxCount: childCount }] : []),
      ...(infantCount > 0 ? [{ paxCode: 'INF', paxCount: infantCount }] : []),
    ],
  });

  const handleSubmit = () => {
    setFormError(null);

    if (tripType === 'MultiCity') {
      const segments: AirSearchSegment[] = [];
      for (const [i, leg] of legs.entries()) {
        if (!leg.origin?.code || !leg.destination?.code) {
          setFormError(`${i + 1}. uçuş için kalkış ve varış havalimanı seçmelisiniz.`);
          return;
        }
        if (!leg.date) {
          setFormError(`${i + 1}. uçuş için tarih seçmelisiniz.`);
          return;
        }
        segments.push({
          sequenceNo: i + 1,
          departureDay: leg.date.format('YYYY-MM-DDT00:00:00'),
          origin: toSearchAirport(leg.origin),
          destination: toSearchAirport(leg.destination),
        });
      }
      onSearch({ ...buildCommon(), airSearchSegments: segments });
      return;
    }

    if (!origin?.code || !destination?.code) {
      setFormError('Kalkış ve varış havalimanı seçmelisiniz.');
      return;
    }
    if (!departureDate) {
      setFormError('Gidiş tarihi seçmelisiniz.');
      return;
    }
    if (tripType === 'RoundTrip' && !returnDate) {
      setFormError('Dönüş tarihi seçmelisiniz.');
      return;
    }

    const originAirport = toSearchAirport(origin);
    const destinationAirport = toSearchAirport(destination);

    onSearch({
      ...buildCommon(),
      airSearchSegments:
        tripType === 'RoundTrip'
          ? [
            {
              sequenceNo: 1,
              departureDay: departureDate.format('YYYY-MM-DDT00:00:00'),
              origin: originAirport,
              destination: destinationAirport,
            },
            {
              sequenceNo: 2,
              departureDay: (returnDate as Dayjs).format('YYYY-MM-DDT00:00:00'),
              origin: destinationAirport,
              destination: originAirport,
            },
          ]
          : [
            {
              sequenceNo: 1,
              departureDay: departureDate.format('YYYY-MM-DDT00:00:00'),
              origin: originAirport,
              destination: destinationAirport,
            },
          ],
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={tripType}
        onChange={(_, value) => value && handleTripTypeChange(value)}
      >
        <ToggleButton value="OneWay">Tek Yön</ToggleButton>
        <ToggleButton value="RoundTrip">Gidiş-Dönüş</ToggleButton>
        <ToggleButton value="MultiCity">Çok Noktalı</ToggleButton>
      </ToggleButtonGroup>

      {formError && <Alert severity="error">{formError}</Alert>}

      {tripType === 'MultiCity' ? (
        <Stack spacing={2}>
          {legs.map((leg, index) => (
            <Stack
              key={index}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
                {index + 1}.
              </Typography>
              <AirportAutocomplete
                label="Nereden"
                value={leg.origin}
                onChange={(value) => updateLeg(index, { origin: value })}
              />
              <AirportAutocomplete
                label="Nereye"
                value={leg.destination}
                onChange={(value) => updateLeg(index, { destination: value })}
              />
              <DatePicker
                label="Tarih"
                value={leg.date}
                onChange={(value) => updateLeg(index, { date: value })}
                minDate={index > 0 ? legs[index - 1].date ?? dayjs() : dayjs()}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <IconButton
                aria-label={`${index + 1}. uçuşu kaldır`}
                size="small"
                disabled={legs.length <= 2}
                onClick={() => removeLeg(index)}
              >
                ✕
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button size="small" onClick={addLeg} disabled={legs.length >= MAX_MULTICITY_LEGS}>
              + Uçuş Ekle
            </Button>
          </Box>
        </Stack>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <AirportAutocomplete label="Nereden" value={origin} onChange={setOrigin} />
            <AirportAutocomplete label="Nereye" value={destination} onChange={setDestination} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Gidiş Tarihi"
              value={departureDate}
              onChange={setDepartureDate}
              minDate={dayjs()}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            {tripType === 'RoundTrip' && (
              <DatePicker
                label="Dönüş Tarihi"
                value={returnDate}
                onChange={setReturnDate}
                minDate={departureDate ?? dayjs()}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            )}
          </Stack>
        </>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Yetişkin"
          type="number"
          size="small"
          value={adultCount}
          onChange={(e) => setAdultCount(Math.max(1, Number(e.target.value) || 1))}
          slotProps={{ htmlInput: { min: 1, max: 9 } }}
        />
        <TextField
          label="Çocuk"
          type="number"
          size="small"
          value={childCount}
          onChange={(e) => setChildCount(Math.max(0, Number(e.target.value) || 0))}
          slotProps={{ htmlInput: { min: 0, max: 9 } }}
        />
        <TextField
          label="Bebek"
          type="number"
          size="small"
          value={infantCount}
          onChange={(e) => setInfantCount(Math.max(0, Number(e.target.value) || 0))}
          slotProps={{ htmlInput: { min: 0, max: 9 } }}
        />
        <TextField
          select
          label="Kabin"
          size="small"
          value={cabinType}
          onChange={(e) => setCabinType(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {CABIN_TYPES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <PersonnelAutocomplete value={employee} onChange={setEmployee} />
      </Stack>

      <Box>
        <Button variant="contained" disabled={loading} onClick={handleSubmit}>
          {loading ? 'Aranıyor…' : 'Uçuş Ara'}
        </Button>
      </Box>

    </Box>
  );
}
